import { LinkButton } from '@/components/elements/forms/buttons';
import Icon from '@/components/icons/Icon';
import useMobileView from '@/hooks/useMobileView';
import { _t } from '@/locale';
import { MouseEvent } from 'react';
import { Link, useLocation } from 'react-router-dom';

type PaginationProps = {
  results: number;
  page?: number;
  itemsPerPage: number;
  nextPage?: (e: MouseEvent<HTMLAnchorElement>) => void;
  prevPage?: (e: MouseEvent<HTMLAnchorElement>) => void;
};

const PageLink = ({
  page,
  className,
  text,
  onClick,
  icon,
}: {
  page: number;
  className?: string;
  text?: React.ReactNode;
  onClick: (e: MouseEvent<HTMLAnchorElement>) => void;
  icon: React.ReactNode;
}) => {
  const location = useLocation();
  const { isMobileView } = useMobileView();
  const searchParams = new URLSearchParams(location.search);
  searchParams.set('page', page.toString());

  return !isMobileView ? (
    <Link to={`${location.pathname}?${searchParams.toString()}`} className={className} onClick={onClick}>
      {icon}
    </Link>
  ) : (
    <LinkButton
      to={`${location.pathname}?${searchParams.toString()}`}
      variant="primary"
      rightIcon={icon}
      onClick={onClick}>
      {text}
    </LinkButton>
  );
};

const Pagination = ({ results, page = 0, itemsPerPage, nextPage, prevPage }: PaginationProps) => {
  const { isMobileView } = useMobileView();
  if (!results || results <= itemsPerPage) return null;
  const total = Math.ceil(results / itemsPerPage);

  return isMobileView ? (
    <div className="pagination px-md py-sm flex flex-row items-center justify-between">
      <div className="flex w-[120px] justify-start">
        {prevPage && <PageLink page={page - 1} icon={<Icon variant="chevron-left" />} onClick={prevPage} />}
      </div>
      <span className="flex-1 text-center font-semibold">{page + 1 + _t('of') + total}</span>
      {nextPage && (
        <div className="flex w-[120px] justify-end">
          <PageLink
            icon={<Icon variant="chevron-right" />}
            page={page + 1}
            onClick={nextPage}
            text={_t('next')}></PageLink>
        </div>
      )}
    </div>
  ) : (
    <div className="flex items-center">
      <nav>
        <ul className="flex list-none items-center rounded">
          <li>
            {prevPage ? (
              <PageLink
                onClick={prevPage}
                className={`border-black-200 flex h-8 w-9 items-center justify-center rounded border bg-white shadow outline-none`}
                page={page - 1}
                icon={<Icon variant="chevron-left" />}></PageLink>
            ) : (
              <span
                className={`border-black-200 bg-black-200 flex h-8 w-9 items-center justify-center rounded border 
                outline-none
              `}>
                <Icon variant="chevron-left" color="fg_disabled" />
              </span>
            )}
          </li>
          <li>
            {nextPage ? (
              <PageLink
                className={`border-black-200 flex h-8 w-9 items-center justify-center rounded border bg-white shadow outline-none`}
                page={page + 1}
                onClick={nextPage}
                icon={<Icon variant="chevron-right" />}></PageLink>
            ) : (
              <span
                className={`border-black-200 bg-black-200 flex h-8 w-9 items-center justify-center rounded border outline-none`}>
                <Icon variant="chevron-right" color="fg_disabled" />
              </span>
            )}
          </li>
        </ul>
      </nav>
      <p className="text-black-600 ml-sm text-sm">
        {_t('paginationInfo1')}{' '}
        <span>
          {page * itemsPerPage + 1} -{' '}
          {page * itemsPerPage + itemsPerPage > results ? results : page * itemsPerPage + itemsPerPage}
        </span>{' '}
        {_t('paginationInfo2')} <span>{results}</span>
      </p>
    </div>
  );
};

export default Pagination;
